// =====================================================================
// Faz Y (2026-05-06) — Category & Brand Sales Performance
// =====================================================================
// "Hangi kategori/marka en çok ciro yapıyor, sezonsal trend var mı"
// sorusunu cevaplar. Owner stok/reklam/tedarikçi planlama için kullanır.
//
// 4 panel:
//   1. KPI özeti (toplam ciro/sipariş + top kategori)
//   2. Kategori bazlı tablo (top 20) — her kategorinin ciro yüzdesi
//   3. Marka bazlı tablo (top 15)
//   4. Top 5 kategori 6-aylık trend mini-line-chart
//
// Endpoint: GET /api/admin/insights/category-sales?days=N
// =====================================================================
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, ToggleButton,
  ToggleButtonGroup, LinearProgress, Avatar
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CategoryIcon from "@mui/icons-material/Category";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import API from "../services/api";

// ──────────────────────────────────────────────────────────────────────
// Formatter'lar — sayfaya özgü
// ──────────────────────────────────────────────────────────────────────
const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// "2026-05" → "May 26"
const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

// Top 5 kategori için renk paleti — sabit sırada (kategori değişmesin renk)
const TREND_COLORS = ["#1565C0", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

export default function CategorySales() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(90);

  const load = (d = days) => {
    setLoading(true);
    API.get(`/api/admin/insights/category-sales?days=${d}`)
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  // ──────────────────────────────────────────────────────────────────
  // Trend chart için: ay sırası + kategori bazlı seri
  // Backend ay × kategori şeklinde flat array dönüyor — pivot'la
  // ──────────────────────────────────────────────────────────────────
  const trendChart = useMemo(() => {
    if (!data?.category_trend || data.category_trend.length === 0) return null;

    // Tüm aylar (sıralı) + tüm kategoriler
    const months = [...new Set(data.category_trend.map(t => t.month))].sort();
    const categoryMap = new Map();  // category_id → { name, byMonth: {month: revenue} }

    for (const t of data.category_trend) {
      const cid = t.category_id;
      if (!categoryMap.has(cid)) {
        categoryMap.set(cid, { id: cid, name: t.category_name, byMonth: {} });
      }
      categoryMap.get(cid).byMonth[t.month] = Number(t.revenue) || 0;
    }

    // Her kategori için aylar dizisi (eksik ay = 0)
    const series = Array.from(categoryMap.values()).map((cat, idx) => ({
      ...cat,
      color: TREND_COLORS[idx % TREND_COLORS.length],
      values: months.map(m => cat.byMonth[m] || 0)
    }));

    // Tüm değerler için global max (chart bar yüksekliği oranı)
    const allValues = series.flatMap(s => s.values);
    const max = Math.max(...allValues, 1);

    return { months, series, max };
  }, [data]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const totalRev = Number(data.total_revenue) || 0;
  const totalOrd = Number(data.total_orders) || 0;
  const topCat = data.category_sales?.[0];
  const topBrand = data.brand_sales?.[0];

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Kategori & Marka Performansı</Typography>
          <Typography variant="body2" color="text.secondary">
            Hangi kategori en çok ciro yapıyor, hangi marka tutuyor
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={30}>30g</ToggleButton>
            <ToggleButton value={90}>90g</ToggleButton>
            <ToggleButton value={180}>6 ay</ToggleButton>
            <ToggleButton value={365}>1 yıl</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile">
            <IconButton onClick={() => load(days)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Üst KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Ciro</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{fmtMoney(totalRev)}</Typography>
            <Typography variant="caption" color="text.disabled">son {days} gün</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Sipariş</Typography>
            <Typography variant="h4" fontWeight={800}>{fmtNum(totalOrd)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>👑 EN ÇOK SATAN KATEGORİ</Typography>
            {topCat ? (
              <>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{topCat.category_name}</Typography>
                <Typography variant="caption" color="#10b981" fontWeight={700}>
                  {fmtMoney(topCat.total_revenue)} · %{totalRev > 0 ? Math.round((Number(topCat.total_revenue) / totalRev) * 100) : 0}
                </Typography>
              </>
            ) : <Typography color="text.disabled">veri yok</Typography>}
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fef3c7", border: "1px solid #fde68a" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>🏆 EN ÇOK SATAN MARKA</Typography>
            {topBrand ? (
              <>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{topBrand.brand}</Typography>
                <Typography variant="caption" color="#f59e0b" fontWeight={700}>
                  {fmtMoney(topBrand.total_revenue)} · {topBrand.order_count} sipariş
                </Typography>
              </>
            ) : <Typography color="text.disabled">veri yok</Typography>}
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        {/* Kategori bazlı tablo */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
              <CategoryIcon sx={{ color: "#1565C0" }} />
              <Typography variant="h6" fontWeight={700}>Kategori Performansı</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={`Top ${data.category_sales.length}`} size="small" />
            </Box>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell><strong>Kategori</strong></TableCell>
                  <TableCell align="right"><strong>Sipariş</strong></TableCell>
                  <TableCell align="right"><strong>Adet</strong></TableCell>
                  <TableCell align="right"><strong>Ciro</strong></TableCell>
                  <TableCell sx={{ minWidth: 100 }}><strong>Pay</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.category_sales.map((c, i) => {
                  const pct = totalRev > 0 ? (Number(c.total_revenue) / totalRev) * 100 : 0;
                  return (
                    <TableRow key={c.category_id} hover>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={i < 3 ? 700 : 500}>
                          {i < 3 && <span style={{ marginRight: 6 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</span>}
                          {c.category_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmtNum(c.order_count)}</TableCell>
                      <TableCell align="right">{fmtNum(c.total_quantity)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#1565C0" }}>
                        {fmtMoney(c.total_revenue)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ flex: 1, height: 6, bgcolor: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                            <Box sx={{
                              width: `${pct}%`, height: "100%",
                              bgcolor: i < 3 ? "#1565C0" : "#94a3b8",
                              transition: "width 0.3s"
                            }} />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: 40, textAlign: "right", fontWeight: 700 }}>
                            %{pct.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </Grid>

        {/* Marka bazlı tablo */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#fef3c7", borderBottom: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 1 }}>
              <BrandingWatermarkIcon sx={{ color: "#f59e0b" }} />
              <Typography variant="h6" fontWeight={700}>Marka Performansı</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={`Top ${data.brand_sales.length}`} size="small" sx={{ bgcolor: "#f59e0b", color: "white" }} />
            </Box>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell><strong>Marka</strong></TableCell>
                  <TableCell align="right"><strong>Sipariş</strong></TableCell>
                  <TableCell align="right"><strong>Ciro</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.brand_sales.map((b, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={i < 3 ? 700 : 500}>
                        {i < 3 && <span style={{ marginRight: 6 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</span>}
                        {b.brand}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" fontSize={11}>
                        {b.unique_customers} müşteri · {fmtNum(b.total_quantity)} adet
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{fmtNum(b.order_count)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#f59e0b" }}>
                      {fmtMoney(b.total_revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>

      {/* En çok sepete eklenen ürünler — talep sinyali (sipariş tamamlanmadan) */}
      {data.top_cart_products && data.top_cart_products.length > 0 && (
        <Card sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: "#eff6ff", borderBottom: "1px solid #dbeafe", display: "flex", alignItems: "center", gap: 1 }}>
            <ShoppingCartIcon sx={{ color: "#3b82f6" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>En Çok Sepete Eklenen Ürünler</Typography>
              <Typography variant="caption" color="text.secondary">
                Talep sinyali — sipariş tamamlanmamış da olsa müşteri ilgisi gösterir
              </Typography>
            </Box>
            <Chip label={`Top ${data.top_cart_products.length}`} size="small" sx={{ bgcolor: "#3b82f6", color: "white" }} />
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Ürün</strong></TableCell>
                <TableCell align="center"><strong>Sepete Ekleyen</strong></TableCell>
                <TableCell align="center"><strong>Sipariş Eden</strong></TableCell>
                <TableCell align="center"><strong>Conversion</strong></TableCell>
                <TableCell align="right"><strong>Stok</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.top_cart_products.map((p, i) => {
                // Conversion rate: sipariş eden / sepete ekleyen
                // Yüksek = ürün başarılı (sepete eklenince satışa dönüşüyor)
                // Düşük = engel var (fiyat/kargo/stok problemi)
                const cartUsers = Number(p.unique_users_added) || 0;
                const orderedCount = Number(p.ordered_count) || 0;
                // Conversion için: sepete ekleyen kişi sayısı baz alınır
                // (aynı kişi 2x eklerse 1 saysın)
                const convPct = cartUsers > 0 ? Math.round((orderedCount / cartUsers) * 100) : 0;
                // Renk eşiği: %70+ yeşil (çok iyi), %40-70 mavi, %20-40 sarı, <20 kırmızı
                const convColor = convPct >= 70 ? "#10b981"
                                : convPct >= 40 ? "#3b82f6"
                                : convPct >= 20 ? "#f59e0b" : "#ef4444";
                const stockLow = Number(p.stock) <= 10;
                return (
                  <TableRow key={p.product_id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Typography fontSize={14} fontWeight={700} sx={{ color: i < 3 ? "#3b82f6" : "#94a3b8", minWidth: 24 }}>
                          {i + 1}
                        </Typography>
                        <Avatar src={p.image_url} variant="rounded" sx={{ width: 36, height: 36, bgcolor: "#f1f5f9" }}>📦</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.2 }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" fontSize={11}>
                            {p.brand} · {fmtMoney(p.price)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} fontSize={14}>{cartUsers}</Typography>
                      <Typography variant="caption" color="text.disabled" fontSize={10}>
                        kişi · {fmtNum(p.total_added_qty)} adet
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} fontSize={14}>{orderedCount}</Typography>
                      <Typography variant="caption" color="text.disabled" fontSize={10}>
                        sipariş
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`%${convPct}`}
                        size="small"
                        sx={{
                          bgcolor: convColor, color: "white",
                          fontWeight: 800, minWidth: 50, fontSize: 11
                        }}
                        title={`${orderedCount}/${cartUsers} dönüşüm oranı`}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={fmtNum(p.stock)}
                        size="small"
                        sx={{
                          bgcolor: stockLow ? "#fef2f2" : "#f0fdf4",
                          color: stockLow ? "#ef4444" : "#10b981",
                          fontWeight: 700, fontSize: 11
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* Açıklama notu */}
          <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
            <Typography variant="caption" color="text.secondary">
              💡 <strong>Conversion %</strong>: Sepete ekleyen kişilerin yüzde kaçı siparişi tamamladı.
              <strong> Düşük conversion</strong> (&lt;%40) = ilgi var ama engel var (fiyat, stok, kargo). Bu ürünlerde fırsat.
              <strong> Yüksek conversion</strong> (&gt;%70) = sepetteyken satılıyor — stok azalmamasını izle.
            </Typography>
          </Box>
        </Card>
      )}

      {/* Top 5 kategori 6-ay trend chart */}
      {trendChart && trendChart.months.length > 0 && (
        <Card sx={{ p: 2.5, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Top 5 Kategori — Son 6 Ay Trend
          </Typography>

          {/* Multi-bar chart: her ay için 5 kategori barı yan yana */}
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 500, display: "flex", alignItems: "flex-end", gap: 2, height: 160 }}>
              {trendChart.months.map((m, mIdx) => (
                <Box key={m} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  {/* Ay içinde 5 kategori barı yan yana */}
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.3, height: 130, width: "100%", justifyContent: "center" }}>
                    {trendChart.series.map((s, sIdx) => {
                      const val = s.values[mIdx] || 0;
                      const heightPct = (val / trendChart.max) * 100;
                      return (
                        <Tooltip key={sIdx} title={`${s.name} ${monthLabel(m)}: ${fmtMoney(val)}`} placement="top">
                          <Box sx={{
                            flex: 1, maxWidth: 16,
                            height: `${Math.max(heightPct, 1)}%`,
                            bgcolor: s.color,
                            borderRadius: "2px 2px 0 0",
                            transition: "height 0.3s",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.7 }
                          }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                  <Typography variant="caption" sx={{ mt: 0.5, fontSize: 10, color: "text.disabled" }}>
                    {monthLabel(m)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2, justifyContent: "center" }}>
            {trendChart.series.map((s, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: s.color, borderRadius: 1 }} />
                <Typography variant="caption" fontSize={11}>{s.name}</Typography>
              </Box>
            ))}
          </Box>
        </Card>
      )}
    </Box>
  );
}
